import { ReactNode, useEffect, useState, useMemo } from "react";
import exxonMobilLogo from "@/assets/exxonmobil-logo.png";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NotificationBell from "@/components/NotificationBell";
import PortalCompanySwitcher from "@/components/PortalCompanySwitcher";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { CLIENT_DISPLAY_NAMES, LESSONS_LEARNED_CLIENTS } from "@/data/portalMockData";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  LayoutDashboard, Ship, Anchor, Shield, FileText, DollarSign,
  BarChart3, Star, Users, Leaf, GraduationCap, Siren, Wrench,
  MessageSquare, Settings, LogOut, ChevronLeft, ChevronRight,
  Home, Menu, X, User, Bell, BookOpen, Navigation, Sparkles
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

// Map each nav href to its permission column
const hrefPermissionMap: Record<string, string> = {
  "/portal": "page_dashboard",
  "/portal/fleet": "page_fleet",
  "/portal/voyages": "page_voyage_ops",
  "/portal/crew": "page_crew_personnel",
  "/portal/crew-transfer-intelligence": "page_cti",
  "/portal/hseq": "page_compliance",
  "/portal/training": "page_training",
  "/portal/emergency": "page_emergency",
  "/portal/finance": "page_commercial",
  "/portal/documents": "page_documents",
  "/portal/esg": "page_esg",
  "/portal/reports": "page_reports",
  "/portal/satisfaction": "page_satisfaction",
  "/portal/maintenance": "page_maintenance",
  "/portal/lessons-learned": "page_lessons_learned",
  "/portal/messages": "page_messages",
  "/portal/questions": "page_questions",
  "/portal/progress": "page_progress",
  "/portal/news": "page_news",
};

const portalNav = [
  { section: "Overview", items: [
    { label: "Dashboard", href: "/portal", icon: LayoutDashboard },
    { label: "Commercial Value Center", href: "/portal/value-center", icon: Sparkles },
  ]},
  { section: "Operations", items: [
    { label: "Fleet & Vessels", href: "/portal/fleet", icon: Ship },
    { label: "Voyage Operations", href: "/portal/voyages", icon: Anchor },
    { label: "Crew & Personnel", href: "/portal/crew", icon: Users },
    { label: "Crew Transfer Intelligence", href: "/portal/crew-transfer-intelligence", icon: Navigation },
    { label: "Chem Carriage", href: "/portal/chem-carriage", icon: BookOpen },
  ]},
  { section: "Safety & Compliance", items: [
    { label: "HSEQ & Compliance", href: "/portal/hseq", icon: Shield },
    { label: "Training & Competency", href: "/portal/training", icon: GraduationCap },
    { label: "Emergency & SAR", href: "/portal/emergency", icon: Siren },
  ]},
  { section: "Commercial", items: [
    { label: "Commercial & Finance", href: "/portal/finance", icon: DollarSign },
    { label: "Documents", href: "/portal/documents", icon: FileText },
    { label: "ESG & Emissions", href: "/portal/esg", icon: Leaf },
  ]},
  { section: "Insights", items: [
    { label: "Reports & Analytics", href: "/portal/reports", icon: BarChart3 },
    { label: "Client Satisfaction", href: "/portal/satisfaction", icon: Star },
    { label: "Maintenance & PMS", href: "/portal/maintenance", icon: Wrench },
    { label: "Lessons Learned", href: "/portal/lessons-learned", icon: BookOpen, clientOnly: ["Client A"] },
  ]},
  { section: "Communication", items: [
    { label: "Messages & Notices", href: "/portal/messages", icon: MessageSquare },
  ]},
];



const PortalLayout = ({ children }: { children: ReactNode }) => {
  const { user, loading, signOut, fullName, isAdmin, isApproved, isAuditor } = useAuth();
  const { companyName } = usePortalCompany();
  const navigate = useNavigate();
  const location = useLocation();
  useSessionTimeout();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch user permissions for portal module visibility
  const { data: userPermissions } = useQuery({
    queryKey: ["portal-user-permissions", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("user_permissions")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user?.id && !isAdmin,
  });

  // Determine which mock client key matches current portal company
  const currentClientKey = useMemo(() => {
    if (!companyName) return null;
    const entry = Object.entries(CLIENT_DISPLAY_NAMES).find(([, display]) =>
      companyName.toLowerCase().includes(display.toLowerCase().split(" ")[0])
    );
    return entry ? entry[0] : null;
  }, [companyName]);

  // Filter nav items — hide by permissions and client-only rules
  const filteredNav = useMemo(() => {
    return portalNav.map(section => ({
      ...section,
      items: section.items.filter(item => {
        // Client-only filter (e.g. Lessons Learned)
        if ((item as any).clientOnly) {
          if (isAdmin) { /* admins see everything */ }
          else if (!currentClientKey || !(item as any).clientOnly.includes(currentClientKey)) return false;
        }
        // Permission-based filter (skip for admins)
        if (!isAdmin && userPermissions) {
          const permKey = hrefPermissionMap[item.href];
          if (permKey && permKey in userPermissions) {
            return (userPermissions as any)[permKey] === true;
          }
        }
        return true;
      }),
    })).filter(section => section.items.length > 0);
  }, [currentClientKey, isAdmin, userPermissions]);

  const allNavItems = useMemo(() => filteredNav.flatMap(s => s.items), [filteredNav]);

  useEffect(() => {
    if (!loading && !user) navigate("/client-login");
    if (!loading && user && isAuditor) navigate("/auditor");
  }, [user, loading, isAuditor, navigate]);

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  if (loading) {
    return (
      <div className="portal-scope">
        <div className="min-h-screen portal-canvas flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[hsl(211,100%,40%)]/30 border-t-[hsl(211,100%,40%)] rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) return null;

  if (!isApproved && !isAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 bg-navy-deep flex items-center justify-center mt-16 lg:mt-20">
          <div className="text-center max-w-md mx-6">
            <div className="w-16 h-16 rounded-2xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <Home className="w-8 h-8 text-yellow-400" />
            </div>
            <h2 className="font-display text-2xl text-primary-foreground mb-3">Account Pending Approval</h2>
            <p className="text-primary-foreground/40 font-body text-sm mb-6">
              Your account is awaiting approval. You'll receive an email at <span className="text-teal">{user.email}</span> once confirmed.
            </p>
            <button onClick={() => signOut().then(() => navigate("/"))} className="px-6 py-2.5 text-xs font-bold tracking-wide uppercase text-primary-foreground/40 hover:text-primary-foreground transition-colors">
              Sign Out
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="portal-scope">
      <div className="min-h-screen flex">
        {/* Desktop Sidebar */}
        <aside className={`hidden lg:flex flex-col portal-sidebar-bg transition-all duration-300 sticky top-0 h-screen ${sidebarCollapsed ? "w-[68px]" : "w-[280px]"}`}>
          {/* Logo / Brand */}
          <div className={`border-b border-white/5 ${sidebarCollapsed ? "p-3" : "p-4"}`}>
            {sidebarCollapsed ? (
              <div className="bg-white rounded-lg p-1 flex justify-center">
                <img src="/vel-io-logo.png" alt="Vel-iO" className="h-6 w-auto object-contain" />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                  <img src="/vel-io-logo.png" alt="Vel-iO" className="h-8 w-auto object-contain mx-auto" />
                </div>
                <p className="text-[9px] text-white/60 tracking-[2px] uppercase text-center font-medium">A Velocity Offshore Platform</p>
                <p className="text-[11px] text-white/85 tracking-[3px] uppercase text-center font-semibold pt-1">Client Portal</p>
              </div>
            )}
          </div>

          {/* User info */}
          {!sidebarCollapsed && (
            <div className="px-4 py-3 border-b border-white/5">
              <p className="text-sm text-white/80 font-medium truncate">{fullName || user.email}</p>
              <p className="text-[10px] text-white/30 truncate">{companyName}</p>
            </div>
          )}

          {/* Nav sections */}
          <nav className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
            {filteredNav.map((section) => (
              <div key={section.section}>
                {!sidebarCollapsed && (
                  <p className="px-3 pt-4 pb-1.5 text-[9px] font-bold tracking-[2px] uppercase text-white/20">
                    {section.section}
                  </p>
                )}
                {section.items.map((item) => {
                  const active = location.pathname === item.href;
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      title={sidebarCollapsed ? item.label : undefined}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 ${
                        active
                          ? "bg-[hsl(211,100%,40%)]/15 text-[hsl(211,100%,40%)] border-l-2 border-[hsl(211,100%,40%)]"
                          : "text-white/40 hover:text-white/70 hover:bg-white/5"
                      } ${sidebarCollapsed ? "justify-center px-0" : ""}`}
                    >
                      <Icon className="w-[16px] h-[16px] flex-shrink-0" />
                      {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Bottom actions */}
          <div className="p-2 border-t border-white/5 space-y-0.5">
            <Link
              to="/portal/admin"
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] text-white/30 hover:text-white/60 transition-all ${sidebarCollapsed ? "justify-center px-0" : ""}`}
            >
              <Settings className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && "Admin & Settings"}
            </Link>
            <button
              onClick={() => signOut().then(() => navigate("/"))}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] text-white/30 hover:text-red-400 transition-all ${sidebarCollapsed ? "justify-center px-0" : ""}`}
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              {!sidebarCollapsed && "Sign Out"}
            </button>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center py-2 text-white/20 hover:text-white/40 transition-colors"
            >
              {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>
        </aside>

        {/* Mobile bottom nav */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 portal-sidebar-bg border-t border-white/10">
          <div className="flex items-center justify-around py-2">
            {allNavItems.slice(0, 4).map((item) => {
              const active = location.pathname === item.href;
              const Icon = item.icon;
              return (
                <Link key={item.href} to={item.href} className={`flex flex-col items-center gap-1 px-3 py-1.5 ${active ? "text-[hsl(211,100%,40%)]" : "text-white/35"}`}>
                  <Icon className="w-5 h-5" />
                  <span className="text-[9px]">{item.label.split(" ")[0]}</span>
                </Link>
              );
            })}
            <button onClick={() => setMobileMenuOpen(true)} className="flex flex-col items-center gap-1 px-3 py-1.5 text-white/35">
              <Menu className="w-5 h-5" />
              <span className="text-[9px]">More</span>
            </button>
          </div>
        </div>

        {/* Mobile slide-out */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-[60] flex">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
            <div className="relative ml-auto w-72 portal-sidebar-bg h-full p-5 flex flex-col overflow-y-auto">
              <button onClick={() => setMobileMenuOpen(false)} className="self-end mb-4 text-white/40">
                <X className="w-5 h-5" />
              </button>
              <div className="mb-4">
                <p className="text-sm text-white/80 font-medium">{fullName || user.email}</p>
                <p className="text-[10px] text-white/30">{companyName}</p>
              </div>
              <nav className="flex-1 space-y-0.5">
                {filteredNav.map((section) => (
                  <div key={section.section}>
                    <p className="px-3 pt-3 pb-1 text-[9px] font-bold tracking-[2px] uppercase text-white/20">{section.section}</p>
                    {section.items.map((item) => {
                      const active = location.pathname === item.href;
                      const Icon = item.icon;
                      return (
                        <Link key={item.href} to={item.href} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${active ? "bg-[hsl(211,100%,40%)]/15 text-[hsl(211,100%,40%)]" : "text-white/40"}`}>
                          <Icon className="w-4 h-4" /> {item.label}
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </nav>
              <div className="border-t border-white/5 pt-3 space-y-2">
                <NotificationBell />
                <button onClick={() => signOut().then(() => navigate("/"))} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-white/30 hover:text-red-400">
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main content area */}
        <main className="flex-1 min-w-0 overflow-y-auto portal-canvas">
          {/* Top bar */}
          <div className="sticky top-0 z-30 bg-[hsl(220,20%,97%)]/95 backdrop-blur-md border-b border-[hsl(220,15%,90%)]">
            <div className="flex items-center justify-between px-6 lg:px-8 py-3">
              <div className="flex items-center gap-2">
                {currentClientKey === "Client A" ? (
                  <img src={exxonMobilLogo} alt="ExxonMobil" className="h-40 w-auto object-contain" />
                ) : (
                  <>
                    <Anchor className="w-4 h-4 text-[hsl(211,100%,40%)]/40" />
                    <span className="text-[11px] font-bold tracking-[2px] uppercase text-[hsl(215,25%,17%)]/30">
                      {companyName || "Client Portal"}
                    </span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-3">
                <NotificationBell />
                <Link to="/portal/settings" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <div className="w-7 h-7 rounded-full bg-[hsl(211,100%,40%)]/10 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-[hsl(211,100%,40%)]" />
                  </div>
                  <span className="hidden sm:inline text-xs text-[hsl(215,25%,17%)]/50">{fullName || user.email}</span>
                </Link>
              </div>
            </div>
          </div>

          <div className="px-5 lg:px-8 py-6 pb-24 lg:pb-8">
            <PortalCompanySwitcher />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PortalLayout;
